import "./ShareFeedback.scss"

import React, { useEffect, useRef, useState } from "react"
import { SignInControl } from "@fider/components/common/SignInControl"
import { Modal, CloseIcon, Form, Button, Input, LegalFooter, Select, SelectOption, DisplayError, Icon } from "@fider/components/common"
import IconCheck from "@fider/assets/images/heroicons-check.svg"
import { useFider } from "@fider/hooks"
import { Trans } from "@lingui/react/macro"
import { actions, Failure, querystring, classSet, cache } from "@fider/services"
import { plainText } from "@fider/services/markdown"
import { i18n } from "@lingui/core"
import { Tag } from "@fider/models"
import { SimilarPosts } from "../components/SimilarPosts"
import { TagsSelect } from "@fider/components/common/TagsSelect"
import CommentEditor from "@fider/components/common/form/CommentEditor"
import { AiSuggestion } from "@fider/components/common/form/AiSuggestion"
import {
  CACHE_KEYS,
  clearCache,
  getCachedDescription,
  getCachedTags,
  getCachedTitle,
  setCachedDescription,
  setCachedTags,
  setCachedTitle,
  setPostPending,
} from "./PostCache"
import { useAttachments } from "@fider/hooks/useAttachments"
import { getCategories, isCategorySlug, PROVINCES, DEFAULT_PROVINCE } from "@fider/services/khcn"

type CheckStatus = "idle" | "checking" | "passed" | "blocked" | "error"

interface CheckState {
  status: CheckStatus
  signature: string
  reason?: string
  source?: string
  aiAvailable?: boolean
}

interface ShareFeedbackProps {
  isOpen: boolean
  placeholder: string
  onClose: () => void
  tags: Tag[]
  // Hạng mục chọn sẵn từ menu ngoài trang chủ (tùy biến KHCN); rỗng = chọn trong form
  initialCategory?: string
}

export const ShareFeedback: React.FC<ShareFeedbackProps> = (props) => {
  const fider = useFider()
  const { isOpen, onClose } = props

  const getRequestedTagSlugs = (): string[] => {
    const cacheValue = getCachedTags()
    const urlValue = querystring.get("tags")
    const combined = [...cacheValue, ...urlValue.split(",")]
    return Array.from(new Set(combined.map((s) => s.trim()).filter((s) => s.length > 0)))
  }

  // Thẻ thường (không phải hạng mục) đã chọn trước đó
  const getTagsCachedValue = (): Tag[] => {
    if (!canEditTags) {
      return []
    }
    const tagsAsStrings = getRequestedTagSlugs()
    return props.tags.filter((tag) => !isCategorySlug(tag.slug) && tagsAsStrings.includes(tag.slug))
  }

  // Hạng mục đã chọn trước đó (từ bản nháp hoặc từ URL ?tags=)
  const getCategoryCachedValue = (): string => {
    return getRequestedTagSlugs().find((slug) => isCategorySlug(slug)) || ""
  }

  const getTitleManuallyEditedValue = (): boolean => {
    // If the cached title deviates from the description, it means the user manually edited it
    return getCachedTitle() !== getCachedDescription()
  }

  const otherTags = props.tags.filter((tag) => !isCategorySlug(tag.slug))
  const canEditTags = fider.settings.postWithTags && otherTags.length > 0
  // Hỏi tên doanh nghiệp và tỉnh thành nếu người dùng chưa khai (hoặc chưa đăng nhập)
  const showProfileFields = !fider.session.isAuthenticated || !fider.session.user.organization

  const descriptionTemplate = fider.session.tenant.descriptionTemplate || ""
  const hasCachedDraft = getCachedDescription() !== ""
  const prefillTemplate = !hasCachedDraft && descriptionTemplate !== ""

  const [title, setTitle] = useState(getCachedTitle())
  const [description, setDescription] = useState(prefillTemplate ? descriptionTemplate : getCachedDescription())
  const { attachments, handleImageUploaded, getImageSrc, clearAttachments } = useAttachments({
    cacheKey: CACHE_KEYS.ATTACHMENT,
    useLocalStorage: true,
    maxAttachments: 3,
  })
  const [tags, setTags] = useState(getTagsCachedValue())
  const [category, setCategory] = useState(getCategoryCachedValue())
  // Bộ chọn hạng mục chỉ mở rộng khi chưa chọn hoặc người dùng bấm "Đổi"
  const [editingCategory, setEditingCategory] = useState(false)
  const [organization, setOrganization] = useState("")
  const [province, setProvince] = useState(DEFAULT_PROVINCE)
  const [editorValue, setEditorValue] = useState({ value: "", version: 0 })
  // Trạng thái bước kiểm tra nội dung; signature đổi (người dùng sửa) thì phải kiểm tra lại
  const [check, setCheck] = useState<CheckState>({ status: "idle", signature: "" })
  const [error, setError] = useState<Failure | undefined>(undefined)
  const titleRef = useRef<HTMLInputElement>()
  const editorRef = useRef<HTMLDivElement>(null)
  const [titleManuallyEdited, setTitleManuallyEdited] = useState(prefillTemplate ? true : getTitleManuallyEditedValue())
  const [isInitialMount, setIsInitialMount] = useState(true)

  useEffect(() => {
    setIsInitialMount(false)
  }, [])

  // Mở từ menu vòng cung: nhận hạng mục đã chọn, thu gọn bộ chọn trong form
  useEffect(() => {
    if (isOpen && props.initialCategory && isCategorySlug(props.initialCategory)) {
      setCategory(props.initialCategory)
      setCachedTags([props.initialCategory, ...tags.map((tag) => tag.slug)])
      setEditingCategory(false)
    }
  }, [isOpen, props.initialCategory])

  // Handle browser back button
  useEffect(() => {
    if (isOpen) {
      // Push a new state when modal opens
      window.history.pushState({ modalOpen: true }, "", window.location.href)

      const handlePopState = () => {
        // If we're going back and the modal is open, close it
        if (isOpen) {
          onClose()
        }
      }

      window.addEventListener("popstate", handlePopState)

      return () => {
        window.removeEventListener("popstate", handlePopState)
      }
    }
  }, [isOpen, onClose])

  // Handle modal close - go back in history if we pushed a state
  const handleClose = () => {
    // Check if we can go back (and if the previous state was pushed by us)
    if (window.history.state?.modalOpen) {
      window.history.back()
    } else {
      onClose()
    }
  }

  useEffect(() => {
    if (!titleManuallyEdited && !isInitialMount && !description.startsWith("![](fider-image:attachments")) {
      // Find newline in the original markdown content for truncation
      let newlineIndex = Math.min(description.indexOf("\n"), 80)
      if (newlineIndex == -1) {
        newlineIndex = 80
      }

      // Get the truncated markdown content and convert to plain text
      const truncatedMarkdown = description.substring(0, newlineIndex)
      const autoTitle = plainText(truncatedMarkdown)

      handleTitleChange(autoTitle, false)
    }
  }, [description, titleManuallyEdited])

  useEffect(() => {
    // Thiết bị cảm ứng: không tự focus để tránh bật bàn phím và cuộn mất phần chọn hạng mục
    const isTouch = typeof window !== "undefined" && window.matchMedia("(hover: none), (pointer: coarse)").matches
    if (isOpen && editorRef.current && !isTouch) {
      // Small delay to ensure modal is fully rendered
      setTimeout(() => {
        // Focus the editor
        const editorContent = editorRef.current?.querySelector(".ProseMirror")
        if (editorContent) {
          ;(editorContent as HTMLElement).focus()
        }
      }, 100)
    }
  }, [isOpen])

  // Handlers for post input changes
  const handleTitleChange = (value: string, isManualEdit = true) => {
    setTitle(value)
    setCachedTitle(value)
    // If this is a manual edit (not auto-generated from description),
    // mark the title as manually edited so we stop auto-populating
    // If the user clears the title, we still want to allow auto-population
    if (isManualEdit) {
      setTitleManuallyEdited(value !== "")
    }
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault()
    }
  }

  const handleTagsChanged = (newTags: Tag[]) => {
    setCachedTags([category, ...newTags.map((tag) => tag.slug)].filter((slug) => slug !== ""))
    setTags(newTags)
  }

  const handleCategoryChange = (slug: string) => {
    setCategory(slug)
    setCachedTags([slug, ...tags.map((tag) => tag.slug)])
    setEditingCategory(false)
  }

  const selectedCategory = getCategories().find((item) => item.slug === category)
  const showCategoryPicker = editingCategory || !selectedCategory

  const handleProvinceChange = (opt?: SelectOption) => {
    if (opt) {
      setProvince(opt.value)
    }
  }

  const handleDescriptionChange = (value: string) => {
    setCachedDescription(value)
    setDescription(value)
  }

  // Áp dụng gợi ý AI (hoặc hoàn tác / làm lại) vào tiêu đề và nội dung
  const applySuggestion = (newTitle: string | undefined, newText: string) => {
    if (newTitle !== undefined) {
      handleTitleChange(newTitle, true)
    }
    handleDescriptionChange(newText)
    setEditorValue((current) => ({ value: newText, version: current.version + 1 }))
  }

  const onSubmitFeedback = () => {
    setPostPending(true)
  }

  const clearError = () => setError(undefined)

  const finaliseFeedback = async () => {
    if (title && canSubmit && checkStatus === "passed") {
      const minDelay = new Promise((resolve) => setTimeout(resolve, 1000))

      const [result] = await Promise.all([
        actions.createPost(
          title,
          description,
          attachments,
          [category, ...tags.map((tag) => tag.slug)].filter((slug) => slug !== ""),
          showProfileFields ? { organization: organization.trim(), province } : {}
        ),
        minDelay,
      ])

      if (result.ok) {
        clearError()
        clearCache()
        clearAttachments()
        if (!result.data.isApproved) {
          cache.session.set("POST_CREATED_MODERATION", "true")
          if (result.data.moderationReason) {
            cache.session.set("POST_MODERATION_REASON", result.data.moderationReason)
          }
        } else {
          cache.session.set("POST_CREATED_SUCCESS", "true")
        }
        location.href = `/posts/${result.data.number}/${result.data.slug}`
      } else if (result.error) {
        setError(result.error)
      }
    }
  }

  const onCodeVerified = (): void => {
    // User is authenticated - finalize the feedback submission
    finaliseFeedback()
  }

  const handleEditorFocus = () => {
    // This function is called when the editor is focused
    // We don't need to do anything special here
  }

  // Nút gửi luôn hiển thị; liệt kê những gì còn thiếu thay vì giấu nút (tùy biến KHCN)
  const missing: string[] = []
  if (title.replace(/\s+/g, " ").trim().length < 10) {
    missing.push(i18n._({ id: "khcn.newpost.missing.title", message: "a title of at least 10 characters" }))
  }
  if (category === "") {
    missing.push(i18n._({ id: "khcn.newpost.missing.category", message: "a category" }))
  }
  if (showProfileFields && organization.trim() === "") {
    missing.push(i18n._({ id: "khcn.newpost.missing.organization", message: "your company or organization" }))
  }
  const canSubmit = missing.length === 0

  const contentSignature = `${title}\u0000${description}\u0000${category}`
  const checkStatus: CheckStatus = check.signature === contentSignature ? check.status : "idle"
  const aiModerationOn = fider.settings.aiModerationEnabled

  const runCheck = async () => {
    const signature = contentSignature
    setCheck({ status: "checking", signature })
    clearError()
    const result = await actions.checkContent("post", title, description, { category })
    if (!result.ok) {
      setCheck({ status: "error", signature, reason: result.error?.errors?.[0]?.message })
      return
    }
    const data = result.data
    if (data.verdict === "ok") {
      setCheck({ status: "passed", signature, source: data.source, aiAvailable: data.aiAvailable })
      return
    }
    setCheck({ status: "blocked", signature, reason: data.reason, source: data.source, aiAvailable: data.aiAvailable })
    setError({ errors: [{ field: data.field === "title" ? "title" : "description", message: data.reason }] })
  }

  const footerHint = (): { text: string; tone: "muted" | "ok" | "warn" | "error" } => {
    if (!canSubmit) {
      return { text: i18n._({ id: "khcn.newpost.missing", message: "Still needed: {items}", values: { items: missing.join(", ") } }), tone: "muted" }
    }
    switch (checkStatus) {
      case "checking":
        return { text: i18n._({ id: "khcn.newpost.check.checking", message: "Checking content..." }), tone: "muted" }
      case "passed":
        if (aiModerationOn && !check.aiAvailable) {
          return {
            text: i18n._({ id: "khcn.newpost.check.passed.local", message: "Content looks good. AI is busy, so the built-in filter was used." }),
            tone: "warn",
          }
        }
        return { text: i18n._({ id: "khcn.newpost.check.passed", message: "Content looks good, you can submit." }), tone: "ok" }
      case "blocked":
        return {
          text: i18n._({
            id: "khcn.newpost.check.blocked",
            message: "Not ready to publish: {reason} Edit your text and check again.",
            values: { reason: check.reason ?? "" },
          }),
          tone: "error",
        }
      case "error":
        return { text: check.reason || i18n._({ id: "khcn.newpost.check.error", message: "Could not check the content, please try again." }), tone: "error" }
      default:
        return { text: i18n._({ id: "khcn.newpost.check.intro", message: "Your content is checked before it is submitted." }), tone: "muted" }
    }
  }

  return (
    <Modal.Window className="c-share-feedback" isOpen={isOpen} onClose={handleClose} size="large" center={false}>
      <Modal.Header>
        <div className="c-share-feedback__header">
          <h1 className="c-share-feedback__title">
            <Trans id="newpost.modal.title">Share your idea...</Trans>
          </h1>
          <CloseIcon closeModal={handleClose} />
        </div>
      </Modal.Header>
      <Modal.Content>
        <div>
          <div className="c-share-feedback-form">
            <Form error={error}>
              <div className="c-form-field">
                <label>
                  <Trans id="khcn.newpost.category.label">What are you posting?</Trans>
                </label>
                {!showCategoryPicker && selectedCategory ? (
                  <div className="c-category-summary">
                    <span className="c-category-summary__dot" style={{ backgroundColor: `#${selectedCategory.color}` }} />
                    <span className="c-category-summary__label">{selectedCategory.label}</span>
                    <span className="c-category-summary__hint">{selectedCategory.hint}</span>
                    <button type="button" className="c-category-summary__change" onClick={() => setEditingCategory(true)} disabled={fider.isReadOnly}>
                      <Trans id="khcn.newpost.category.change">Change</Trans>
                    </button>
                  </div>
                ) : (
                  <div className="c-category-picker" role="radiogroup">
                    {getCategories().map((item) => (
                      <button
                        type="button"
                        key={item.slug}
                        role="radio"
                        aria-checked={category === item.slug}
                        disabled={fider.isReadOnly}
                        className={classSet({ "c-category-picker__option": true, "c-category-picker__option--active": category === item.slug })}
                        onClick={() => handleCategoryChange(item.slug)}
                      >
                        <span className="c-category-picker__body">
                          <span className="c-category-picker__label">
                            <span className="c-category-picker__dot" style={{ backgroundColor: `#${item.color}` }} />
                            {item.label}
                          </span>
                          <span className="c-category-picker__hint">{item.hint}</span>
                        </span>
                        <span className="c-category-picker__check" aria-hidden="true">
                          <Icon sprite={IconCheck} />
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                <DisplayError fields={["category"]} error={error} />
              </div>
              <div ref={editorRef} className="mb-4">
                <CommentEditor
                  field="description"
                  onChange={handleDescriptionChange}
                  onFocus={handleEditorFocus}
                  initialValue={description}
                  disabled={fider.isReadOnly}
                  maxAttachments={3}
                  maxImageSizeKB={5 * 1024}
                  placeholder={i18n._({
                    id: "newpost.modal.description.placeholder",
                    message: "Tell us about it. Explain it fully, don't hold back, the more information the better.",
                  })}
                  onImageUploaded={handleImageUploaded}
                  onGetImageSrc={getImageSrc}
                  externalValue={editorValue}
                />
                <AiSuggestion kind="post" title={title} text={description} disabled={fider.isReadOnly} onApply={applySuggestion} />
              </div>
              <SimilarPosts title={title} tags={props.tags} />
              <Input
                field="title"
                inputRef={titleRef}
                maxLength={255}
                label={i18n._({ id: "newpost.modal.title.label", message: "Give your idea a title" })}
                value={title}
                disabled={fider.isReadOnly}
                onChange={handleTitleChange}
                onKeyDown={handleKeyDown}
                placeholder={i18n._({ id: "newpost.modal.title.placeholder", message: "Something short and snappy, sum it up in a few words" })}
              />
              {showProfileFields && (
                <div className="c-share-feedback-profile">
                  <Input
                    field="organization"
                    label={i18n._({ id: "khcn.profile.organization.label", message: "Company / organization" })}
                    value={organization}
                    maxLength={200}
                    disabled={fider.isReadOnly}
                    onChange={setOrganization}
                    placeholder={i18n._({ id: "khcn.profile.organization.placeholder", message: "Name of your company, business or agency" })}
                  />
                  <Select
                    field="province"
                    label={i18n._({ id: "khcn.profile.province.label", message: "Province / city" })}
                    defaultValue={province}
                    options={PROVINCES.map((name) => ({ label: name, value: name }))}
                    onChange={handleProvinceChange}
                  />
                </div>
              )}
              {canEditTags && (
                <div className="c-form-field">
                  <label>
                    <Trans id="label.tags">Tags</Trans>
                  </label>
                  <div className={classSet({ "c-form-field": true })}>
                    <TagsSelect tags={otherTags} selectionChanged={handleTagsChanged} selected={tags} alwaysEditing={true} canEdit={true} />
                  </div>
                </div>
              )}
            </Form>
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer align="left">
        {!fider.session.isAuthenticated ? (
          <div className="c-share-feedback-signin">
            <h2 className="text-title text-center mb-4">
              <Trans id="newpost.modal.submit">Submit your idea</Trans>
            </h2>
            <SignInControl
              onSubmit={onSubmitFeedback}
              onCodeVerified={onCodeVerified}
              signInButtonText={i18n._({ id: "signin.message.email", message: "Continue with Email" })}
              useEmail={true}
              redirectTo={fider.settings.baseURL}
            />
            <LegalFooter />
          </div>
        ) : (
          <div className="c-share-feedback__footer">
            <span className={`c-share-feedback__hint c-share-feedback__hint--${footerHint().tone}`}>{footerHint().text}</span>
            {checkStatus === "passed" ? (
              <Button variant="primary" onClick={finaliseFeedback} disabled={!canSubmit || fider.isReadOnly}>
                <Trans id="newpost.modal.submit">Submit your idea</Trans>
              </Button>
            ) : (
              <Button variant="primary" onClick={runCheck} disabled={!canSubmit || fider.isReadOnly || checkStatus === "checking" || checkStatus === "blocked"}>
                {checkStatus === "checking" ? (
                  <Trans id="khcn.newpost.check.checking">Checking content...</Trans>
                ) : checkStatus === "error" ? (
                  <Trans id="khcn.newpost.check.recheck">Check again</Trans>
                ) : (
                  <Trans id="khcn.newpost.check.action">Check content</Trans>
                )}
              </Button>
            )}
          </div>
        )}
      </Modal.Footer>
    </Modal.Window>
  )
}
